import {z} from 'zod';
import {
	editEffectsSortOrder,
	type EditEffectsSortOrderParams,
	getSortField,
	type GetSortFieldParams,
	geSortOrder,
	type GetSortOrderParams,
	SortOrder,
	SortSchema,
} from '@netx/core-next/schema';
import {
	DownloadRequestField,
	type IDownloadRequest,
} from './downloadRequestScheme';

export const DOWNLOAD_REQUEST_SORTABLE_FIELDS = [
	DownloadRequestField.requester,
	DownloadRequestField.requestDate,
	DownloadRequestField.approvalCount,
	DownloadRequestField.reviewStatus,
	DownloadRequestField.portalContext,
] as const;

export const DownloadRequestsSortSchema = SortSchema.omit({
	field: true,
}).extend({
	field: z.enum(DOWNLOAD_REQUEST_SORTABLE_FIELDS),
});

export type DownloadRequestsSort = z.infer<typeof DownloadRequestsSortSchema>;

export const DownloadRequestsSortableField = z.enum(
	DOWNLOAD_REQUEST_SORTABLE_FIELDS,
).enum;

export const DOWNLOAD_REQUESTS_DEFAULT_SORT_FIELD =
	DownloadRequestsSortableField.requestDate;

export const DOWNLOAD_REQUESTS_DEFAULT_SORT_ORDER = SortOrder.DESCENDING;

/** will return the sort passed in if valid/specified, otherwise will return the defaults for the invalid/unspecified */
export const getDownloadRequestsDefaultSort = (
	sort: Partial<DownloadRequestsSort> = {},
): DownloadRequestsSort => ({
	field: getDownloadRequestsSortField({sortField: sort.field}),
	order: getDownloadRequestsSortOrder({sortOrder: sort.order}),
});

/** will return the passed in sort field if valid, otherwise will retutn the default sort field */
export const getDownloadRequestsSortField = (
	params: Omit<
		GetSortFieldParams<typeof DOWNLOAD_REQUEST_SORTABLE_FIELDS[number]>,
		'defaultSortField' | 'sortableFields'
	>,
) =>
	getSortField({
		...params,
		sortableFields: DOWNLOAD_REQUEST_SORTABLE_FIELDS,
		defaultSortField: DOWNLOAD_REQUESTS_DEFAULT_SORT_FIELD,
	});

/** will return the passed in sort order if valid, otherwise will return the default sort order */
export const getDownloadRequestsSortOrder = (
	params: Omit<GetSortOrderParams, 'defaultSortOrder'>,
) =>
	geSortOrder({
		...params,
		defaultSortOrder: DOWNLOAD_REQUESTS_DEFAULT_SORT_ORDER,
	});

/** given the original data, the new data, and the current sort, determine if the sort order was effected */
export const downloadRequestEditEffectsOrder = (
	params: Omit<
		EditEffectsSortOrderParams<
			IDownloadRequest,
			typeof DOWNLOAD_REQUEST_SORTABLE_FIELDS[number]
		>,
		'sortableFields'
	>,
) =>
	editEffectsSortOrder({
		...params,
		sortableFields: DOWNLOAD_REQUEST_SORTABLE_FIELDS,
	});
